import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, CheckCircle, Linkedin } from 'lucide-react';
import axios from 'axios';
import { portfolioData } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { identity, contact } = portfolioData;
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-2">
          <h2 className="text-2xl sm:text-3xl md:text-4xl" style={{ fontFamily: 'Cinzel, serif', color: 'rgba(235, 240, 250, 0.95)', fontWeight: 400 }}>{contact.heading}</h2>
        </motion.div>
        
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="text-center mb-8 sm:mb-12 text-sm sm:text-base" style={{ fontFamily: 'EB Garamond, serif', fontStyle: 'italic', color: 'rgba(180, 195, 220, 0.8)', fontSize: '1rem' }}>
          {contact.subtitle}
        </motion.p>
        
        <motion.div 
          initial={{ opacity: 0, y: 40 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.8 }}
          whileHover={{ scale: 1.01 }}
          className="glass-panel p-6 sm:p-8 md:p-10"
        >
          {isSubmitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center py-8 text-center">
              <CheckCircle className="w-12 h-12 mb-6" style={{ color: 'rgba(180, 200, 230, 0.9)' }} />
              <p className="mb-2" style={{ fontFamily: 'EB Garamond, serif', color: 'rgba(240, 245, 255, 0.95)', fontSize: '1.2rem' }}>{contact.successMessage.title}</p>
              <p style={{ fontFamily: 'EB Garamond, serif', fontStyle: 'italic', color: 'rgba(180, 195, 220, 0.8)' }}>{contact.successMessage.subtitle}</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 rounded-lg text-center" style={{ background: 'rgba(220, 100, 100, 0.15)', color: 'rgba(255, 180, 180, 0.9)', fontFamily: 'DM Mono, monospace', fontSize: '0.85rem' }}>
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name" style={{ fontFamily: 'DM Mono, monospace', color: 'rgba(180, 195, 220, 0.8)', fontSize: '0.85rem' }}>Name</Label>
                <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="contact-input" placeholder="Your name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" style={{ fontFamily: 'DM Mono, monospace', color: 'rgba(180, 195, 220, 0.8)', fontSize: '0.85rem' }}>Email</Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="contact-input" placeholder="your@email.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message" style={{ fontFamily: 'DM Mono, monospace', color: 'rgba(180, 195, 220, 0.8)', fontSize: '0.85rem' }}>Message</Label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="contact-input resize-none" placeholder="Your message..." />
              </div>
              <Button type="submit" disabled={isSubmitting} className="w-full py-5 sm:py-6 submit-button">
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2 text-sm sm:text-base">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-4 h-4 border-2 border-t-transparent rounded-full" style={{ borderColor: 'rgba(230,240,255,0.8)', borderTopColor: 'transparent' }} />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2 text-sm sm:text-base" style={{ fontFamily: 'EB Garamond, serif', fontStyle: 'italic' }}>
                    <Send className="w-4 h-4" />
                    {contact.buttonText}
                  </span>
                )}
              </Button>
            </form>
          )}
          
          {/* Contact links */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 md:gap-8" style={{ borderTop: '1px solid rgba(180, 195, 220, 0.1)' }}>
            <motion.a 
              href={`mailto:${identity.email}`}
              whileHover={{ scale: 1.05, y: -2 }}
              className="inline-flex items-center gap-2 email-link text-xs sm:text-sm" 
              style={{ fontFamily: 'DM Mono, monospace', fontSize: '0.9rem' }}
            >
              <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="break-all">{identity.email}</span>
            </motion.a>
            <motion.a 
              href={identity.linkedin} 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, y: -2 }}
              className="inline-flex items-center gap-2 email-link text-xs sm:text-sm" 
              style={{ fontFamily: 'DM Mono, monospace', fontSize: '0.9rem' }}
            >
              <Linkedin className="w-3 h-3 sm:w-4 sm:h-4" />
              LinkedIn
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
